import { uniqBy } from "lodash-es";
import { Attr, BelongsTo, Model } from "spraypaint";

import { getLocaleText } from "shared/boot/i18n";
import {
  MEDIA_RELEASE_TYPE,
  MEDIA_RELEASE_TYPES,
  PITCH_TYPE,
} from "shared/constants";
import {
  type OutreachCommunicationStatus,
  type OutreachCommunicationStatusType,
} from "shared/resources/OutreachCommunication";
import { ApplicationRecord } from "shared/services/spraypaint";

import {
  getMediaReleaseStatuses,
  type MediaReleaseStatus,
  type MediaReleaseStatusType,
} from "./MediaRelease";
import type Organisation from "./Organisation";
import {
  getOutreachCommunicationStatuses,
  getOutreachCommunicationTypes,
} from "./OutreachCommunication";
import type OutreachCommunicationUser from "./OutreachCommunicationUser";
import type User from "./User";

export type OutreachActivityStatusType =
  | MediaReleaseStatusType
  | OutreachCommunicationStatusType;

export type OutreachActivityStatus =
  | MediaReleaseStatus
  | OutreachCommunicationStatus;

export function outreachActivityStatus(): OutreachActivityStatus[] {
  return uniqBy(
    [...getOutreachCommunicationStatuses(), ...getMediaReleaseStatuses()],
    "value"
  );
}

export function outreachActivityTypes() {
  return [
    {
      color: "#13C3C3",
      label: getLocaleText("outreach.media_release.media_release_title"),
      value: MEDIA_RELEASE_TYPE,
    },
    {
      color: "#9154de",
      label: getLocaleText("outreach.media_release.pitch_title"),
      value: PITCH_TYPE,
    },
    ...getOutreachCommunicationTypes(),
  ];
}

@Model()
class OutreachActivity extends ApplicationRecord {
  static jsonapiType = "outreach_activities";

  @BelongsTo() activity: any;

  @Attr() activityId: number;

  @Attr() activityType: string;

  @BelongsTo() organisation: Organisation;

  @Attr() organisationId: number;

  @Attr() organisationTeamId: number;

  @BelongsTo() user: User;

  @Attr() userId: number;

  assignees() {
    if (this.isMediaRelease()) {
      return [this.activity.user.name];
    }

    return this.activity.outreachCommunicationUsers
      .filter(
        (communicationUser: OutreachCommunicationUser) => communicationUser.user
      )
      .map(
        (communicationUser: OutreachCommunicationUser) =>
          communicationUser.user.name
      );
  }

  contactsCount() {
    if (this.isMediaRelease()) {
      return this.activity.recipientCount;
    }

    return this.activity.outreachCommunicationContactsCount;
  }

  date() {
    if (this.isMediaRelease()) {
      if (this.isMediaReleaseSent()) {
        return this.activity.sentAt;
      }

      if (this.activity.scheduledAt) {
        return this.activity.scheduledAt;
      }

      return null;
    }

    return this.activity.sentAt;
  }

  isMediaRelease() {
    return MEDIA_RELEASE_TYPES.includes(this.activity?.mediaReleaseType);
  }

  isMediaReleaseSent() {
    return this.isMediaRelease() && this.activity.isSent();
  }

  isPitch() {
    return (
      this.isMediaRelease() && this.activity.mediaReleaseType === PITCH_TYPE
    );
  }

  status() {
    return outreachActivityStatus().find(
      (status: OutreachActivityStatus) => status.value === this.activity.status
    );
  }

  title() {
    if (this.isMediaRelease()) {
      if (this.activity.title) {
        return this.activity.title;
      }

      if (this.activity.subject) {
        return this.activity.subject;
      }

      return this.activity.mediaReleaseType === PITCH_TYPE
        ? getLocaleText("outreach.media_release.pitch_title")
        : getLocaleText("outreach.media_release.media_release_title");
    }

    return this.activity.title;
  }

  type() {
    if (this.isMediaRelease()) {
      return outreachActivityTypes().find(
        (type) => type.value === this.activity.mediaReleaseType
      );
    }

    return outreachActivityTypes().find(
      (type) => type.value === this.activity.communicationType
    );
  }
}

export default OutreachActivity;
